import BigNumber from 'bignumber.js';

export function getDecimalSign(
  locale: Intl.UnicodeBCP47LocaleIdentifier,
  options?: Intl.NumberFormatOptions
): string {
  return (
    new Intl.NumberFormat(locale, options)
      .formatToParts(0.1)
      .filter((part: { type: string; value: string }) => part.type === 'decimal')[0]?.value || ''
  );
}

export function getFraction(value: string) {
  const decPos = value.indexOf('.');
  const substrLength = decPos === -1 ? value.length : decPos;
  return value.substring(substrLength);
}

export function getCleanedValue(
  value: string,
  decimalSign: string,
  maximumFractionDigits: number
): string {
  let rawValue = value.replace(new RegExp(`[^${decimalSign}\\d-]+`, 'gu'), '');
  if (decimalSign && decimalSign !== '.') rawValue = rawValue.replace(decimalSign, '.');
  const match = rawValue.match(new RegExp(`-?\\d*(\\.\\d{0,${maximumFractionDigits}})?`));
  if (match && match.length > 0) {
    rawValue = match[0];
  }
  return rawValue;
}

const isValidUnit = (unit: string) => {
  try {
    // eslint-disable-next-line no-new
    new Intl.NumberFormat(undefined, { style: 'unit', unit });
    return true;
  } catch {
    return false;
  }
};

export const getFormattedValue = (
  value: string,
  locale: string,
  unit?: string,
  {
    notation = 'standard',
    useGrouping = false,
    maximumFractionDigits,
    minimumFractionDigits
  }: Pick<
    Intl.NumberFormatOptions,
    'notation' | 'useGrouping' | 'maximumFractionDigits' | 'minimumFractionDigits'
  > = {}
) => {
  const isUnit = unit ? isValidUnit(unit) : false;
  const [integer, fractionPart] = value.split('.', 2) as [string, string?];

  const formatter = new Intl.NumberFormat(locale, {
    notation,
    useGrouping,
    minimumFractionDigits:
      minimumFractionDigits ?? Math.min(maximumFractionDigits ?? 0, fractionPart?.length ?? 0),
    maximumFractionDigits,
    style: isUnit ? 'unit' : undefined,
    unit: isUnit ? unit : undefined,
    numberingSystem: 'latn'
  });

  let formatted = value;

  if (value && !Number.isNaN(parseFloat(integer))) {
    const endsWithDot = fractionPart === '';
    const valueNumber = endsWithDot ? `${value}1` : value;
    formatted =
      formatter
        .formatToParts(valueNumber as `${number}`)
        .reduce((result, { type, value: part }) => {
          if (type === 'fraction' && endsWithDot) return result;
          return result + part;
        }, '') + (!isUnit && unit ? `\u00a0${unit}` : '');
  } else if (unit) {
    if (isUnit)
      formatted = formatter
        .formatToParts(value.startsWith('-') ? -1 : 1)
        .reduce(
          (result, { type, value: part }) =>
            ['unit', 'minusSign'].includes(type) ? result + part : result,
          ''
        );
    else formatted = value.startsWith('-') ? `- ${unit}` : unit;
  }

  return formatted;
};

export function isValueInRange(value: number, min?: number, max?: number): boolean {
  return !((min !== undefined && value < min) || (max !== undefined && value > max));
}

export function getIncrementedValue(value: string, min: number, max: number, step: number): string {
  const numberValue = parseFloat(value) || 0;
  const newValue = new BigNumber(numberValue).plus(step);
  if (isValueInRange(newValue.toNumber(), min, max)) {
    return newValue.toFixed();
  }
  if (min !== undefined && newValue.toNumber() < min) {
    return min.toString();
  }
  return value;
}

export function getDecrementedValue(value: string, min: number, max: number, step: number): string {
  const numberValue = parseFloat(value) || 0;
  const newValue = new BigNumber(numberValue).minus(step);
  if (isValueInRange(newValue.toNumber(), min, max)) {
    return newValue.toFixed();
  }
  if (max !== undefined && newValue.toNumber() > max) {
    return max.toString();
  }
  return value;
}

interface NumberFormatOptions {
  locale: string;
  options?: Intl.NumberFormatOptions;
}

export function formatNumber(value: number | bigint, { locale, options }: NumberFormatOptions) {
  const formatter = new Intl.NumberFormat(locale, options);

  return formatter.format(value);
}
